import React from 'react';
import classNames from 'classnames';
import {FormattedHTMLMessage, useIntl} from 'react-intl';
import Tooltip from '../Tooltip.jsx';
import {renderQuestionErrors} from '../../util/questionUtil.jsx';
import MessageIfPresent from '../MessageIfPresent.jsx';
import PropTypes from 'prop-types';
import {QuestionShape} from '../../shapes/index.mjs';
import {getMessageValue} from '../../i18n/messageUtil.mjs';
import {useText} from '../TextContext';
import {QuestionLayoutContext} from './QuestionComponentContext';

const position = {
  afterLabel: 'afterLabel',
  afterSubtext: 'afterSubtext',
  beforeLabel: 'beforeLabel'
};

const QuestionText = props => {
  const {question, className} = props;
  const cssBEM = 'question';
  const context = React.useContext(QuestionLayoutContext);

  const classes = classNames(className, 'question__text');
  const intl = useIntl();
  const textContext = useText();
  const attributes = question?.attributes;
  const tooltipProps = attributes?.tooltipProps;
  const ErrorTag = attributes?.error?.tag || context?.attributes?.error?.tag;
  const errorProps = attributes?.error?.props || context?.attributes?.error?.props;
  const errorPosition = attributes?.error?.position || context?.attributes?.error?.position || position.beforeLabel;
  const errorIcon = attributes?.error?.icon || context?.attributes?.error?.icon;

  const label = textContext?.[`${question.appkey}`] || (
    <MessageIfPresent id={`${question.appkey}`} key={question.appkey}>
      <FormattedHTMLMessage id={question.appkey} />
    </MessageIfPresent>
  );
  const ariaLabel = getMessageValue(intl.locale, question.appkey);
  const subtext = textContext?.[`${question.appkey}`]?.subtext || (
    <MessageIfPresent id={`${question.appkey}.subtext`}>
      <FormattedHTMLMessage id={`${question.appkey}.subtext`} />
    </MessageIfPresent>
  );
  const tooltip = (
    <MessageIfPresent id={`${question.appkey}.tooltip`} key={`${question.appkey}.tooltip`}>
      <Tooltip
        className={`${cssBEM}__tooltip`}
        messageId={`${question.appkey}.tooltip`}
        {...tooltipProps}
      />
    </MessageIfPresent>
  );

  const error = () => ErrorTag ?
    <ErrorTag key={`${question.appkey}.error`} className={`${cssBEM}__error error`} {...errorProps}>
      {errorIcon}
      {renderQuestionErrors(question)}
    </ErrorTag>
    :
    <Tooltip
      type="error"
      key={`${question.appkey}.error`}
      className={`${cssBEM}__tooltip error`}
      messageId={question.appkey}
      iconRender={<i className="fa fa-exclamation-circle" alt="Error Exclamation Icon" />}
      content={<span>{renderQuestionErrors(question)}</span>}
      {...errorProps}
    />;

  const errorNode = question.errors && (context?.attributes?.error?.node || error());

  const getErrorPosition = () => {
    switch (errorPosition) {
      case `${position.afterLabel}`:
        return [label, tooltip, errorNode];
      case `${position.afterSubtext}`:
        return [label, tooltip];
      case `${position.beforeLabel}`:
      default:
        return [errorNode, label, tooltip];
    }
  };

  return (
    <legend className={classes} aria-label={ariaLabel}>
      <div>
        {getErrorPosition()}
      </div>
      {subtext && <div className="subtext">{subtext}</div>}
      {errorPosition === 'afterSubtext' && errorNode}
    </legend>
  );
};

QuestionText.propTypes = {
  className: PropTypes.string,
  question: QuestionShape,
};

export default QuestionText;
